"use client";

import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import Logo from "./Logo";
import { HugeiconsIcon } from "@hugeicons/react";
import { 
  Menu01Icon, 
  SentIcon,
  Home01Icon,
  CodeIcon,
  Briefcase01Icon,
  Notebook01Icon,
  CompassIcon
} from "@hugeicons/core-free-icons";
import DarkModeSwitch from "./DarkModeSwitch";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Drawer, useOverlayState } from "@heroui/react";

const NavArray = [
  { label: "Home", href: "/", icon: Home01Icon },
  { label: "Services", href: "/services", icon: CodeIcon },
  { label: "Our Work", href: "/our-work", icon: Briefcase01Icon },
  { label: "Blog", href: "/blog", icon: Notebook01Icon },
  { label: "Contact", href: "/contact", icon: SentIcon },
  { label: "About", href: "/about-us", icon: CompassIcon },
];

export default function Navbar() {
  let pathname = usePathname() || "/";

  if (pathname.includes("/writing/")) {
    pathname = "/writing";
  }

  useEffect(() => {
    setSelectedPath(pathname);
  }, [pathname]);

  const [selectedPath, setSelectedPath] = useState(pathname);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const drawerState = useOverlayState();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const DrawerMenu = () => {
    return (
      <>
        <button 
          onClick={drawerState.open}
          className="relative cursor-pointer md:hidden mx-auto my-auto rounded-full font-outfit bg-white/10 dark:bg-black/20 border border-white/20 dark:border-white/5 backdrop-blur-md p-2.5 text-neutral-800 dark:text-neutral-200 transition-all duration-300 hover:scale-105 active:scale-95 hover:bg-blue-500/10 dark:hover:bg-blue-500/20"
        >
          <HugeiconsIcon icon={Menu01Icon} className="w-5 h-5" size={20} />
        </button>
        <Drawer state={drawerState}>
          <Drawer.Backdrop variant="opaque" className="z-[10001]">
            <Drawer.Content placement="bottom" className="z-[10002] w-full p-2 px-3">
              <Drawer.Dialog className="bg-white/95 dark:bg-zinc-950/95 backdrop-blur-xl border-t border-neutral-200/80 dark:border-neutral-800/80 flex flex-col p-6 shadow-2xl rounded-[2.5rem] pb-8">
                <Drawer.Handle className="bg-neutral-300 dark:bg-neutral-700 w-12 h-1.5 rounded-full mx-auto mb-6" />
                
                <div className="flex min-w-full flex-col gap-6">
                  <div className="flex min-w-full justify-between items-center px-1">
                    <span className="text-sm font-outfit font-bold tracking-wider text-neutral-800 dark:text-neutral-200 uppercase">
                      Menu
                    </span>
                    <DarkModeSwitch width="w-4" />
                  </div>
                  
                  {/* Grid Menu Redesign */}
                  <div className="grid grid-cols-3 gap-3">
                    {NavArray.map((item, index) => {
                      const isActive = item.href === selectedPath;
                      return (
                        <Link
                          href={item.href}
                          key={index}
                          className={`flex flex-col items-center justify-center p-4 rounded-2xl border transition-all duration-200 ${
                            isActive
                              ? "bg-blue-50/80 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 font-semibold shadow-sm shadow-blue-500/5"
                              : "bg-neutral-50 dark:bg-zinc-900/50 hover:bg-neutral-100 dark:hover:bg-zinc-900/80 border-neutral-200/80 dark:border-zinc-800/50 text-neutral-800 dark:text-neutral-200"
                          }`}
                          onClick={() => {
                            setSelectedPath(item.href);
                            drawerState.close();
                          }}
                        >
                          <HugeiconsIcon icon={item.icon} className={`w-6 h-6 mb-2 transition-transform duration-300 ${isActive ? "scale-110 text-blue-600 dark:text-blue-400" : "text-neutral-600 dark:text-neutral-400"}`} size={24} />
                          <span className="text-xs font-outfit font-medium tracking-wide">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </Drawer.Dialog>
            </Drawer.Content>
          </Drawer.Backdrop>
        </Drawer>
      </>
    );
  };

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className={`fixed left-0 right-0 mx-auto z-[1000] transition-all duration-500 ease-out ${
        isScrolled 
          ? "top-4 w-[calc(100%-2rem)] max-w-6xl" 
          : "top-0 w-full max-w-full"
      }`}
    >
      <div 
        className={`flex flex-row justify-between items-center transition-all duration-500 ease-out ${
          isScrolled
            ? "px-4 md:px-6 py-2.5 bg-white/20 dark:bg-black/30 border border-white/20 dark:border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] backdrop-blur-xl rounded-full"
            : "px-2 md:px-8 py-4.5 bg-white/10 dark:bg-black/15 rounded-none"
        }`}
      >
        <Link
          href="/"
          aria-label="Devebsy Home"
          className="flex space-x-2 justify-center items-center hover:opacity-90 transition-opacity"
        >
          <Logo />
        </Link>
        
        <div className="hidden md:flex gap-1 items-center">
          <nav className="flex relative justify-start z-[100] gap-0.5">
            {NavArray.map((item, index) => {
              const isActive = item.href === selectedPath;
              return (
                <Link
                  href={item.href}
                  key={index}
                  className={`px-4 py-1.5 font-outfit cursor-pointer rounded-full text-[15px] relative no-underline transition-colors duration-300 ${
                    isActive
                      ? "text-white font-semibold"
                      : "text-neutral-700 dark:text-neutral-200 hover:text-blue-600 dark:hover:text-blue-400"
                  }`}
                  onMouseEnter={() => setHoveredPath(item.href)}
                  onMouseLeave={() => setHoveredPath(null)}
                  onClick={() => {
                    setSelectedPath(item.href);
                  }}
                >
                  <span className="relative z-10">{item.label}</span>
                  
                  {isActive && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 shadow-md shadow-blue-500/20 rounded-full -z-10"
                      layoutId="navbar-active"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 30,
                      }}
                    />
                  )}

                  {!isActive && item.href === hoveredPath && (
                    <motion.div
                      className="absolute inset-0 bg-blue-500/10 dark:bg-blue-500/10 rounded-full -z-10"
                      layoutId="navbar-hover"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 30,
                      }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="flex flex-row gap-3 items-center justify-center">
          <div className="hidden md:flex items-center justify-center">
            <DarkModeSwitch />
          </div>
          
          {selectedPath !== "/contact" && (
            <Link href="/contact">
              <motion.button
                type="button"
                role="button"
                whileTap={{
                  scale: 0.96,
                }}
                whileHover={{
                  scale: 1.02,
                }}
                className="cursor-pointer px-4.5 py-2 flex flex-row gap-2 items-center rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 dark:from-blue-500 dark:via-indigo-500 dark:to-violet-500 hover:opacity-95 text-white shadow-md shadow-blue-500/20 dark:shadow-indigo-500/10 font-outfit text-sm font-semibold transition-all duration-250 backdrop-blur-sm"
              >
                Get in Touch{" "}
                <span>
                  <HugeiconsIcon icon={SentIcon} className="w-3.5 h-3.5" size={14} />
                </span>
              </motion.button>
            </Link>
          )}

          <DrawerMenu />
        </div>
      </div>
    </motion.nav>
  );
}
